import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import { TrendingDown, TrendingUp } from 'lucide-react'
import { cn } from '../../../lib/utils'
import { computeMovingAverage } from '../../../lib/phoenixUtils'
import { useUnits } from '../../../contexts/UnitsContext'
import { isPlausibleWeightKg } from '../../../lib/units'
import { useIsMobile } from '../../../hooks/useIsMobile'
import { useTheme } from '../../../contexts/ThemeContext'

const WINDOWS = [7, 14, 30, 0]
const MAX_TICKS = 5

export function WeightChart({ entries }) {
  const { t } = useTranslation()
  const { weightUnit, displayValueWeight } = useUnits()
  const isMobile = useIsMobile()
  const { primaryHsl } = useTheme()
  const primary500 = primaryHsl(500)
  const [window, setWindow] = useState(7)

  // Drop corrupt rows (e.g. a stray 42857) before the chart sees them —
  // recharts otherwise stretches the y-axis to fit the outlier, even
  // when it sits outside the visible window.
  const sortedAsc = useMemo(
    () => [...entries].reverse().filter((e) => isPlausibleWeightKg(Number(e.weight))),
    [entries]
  )
  const withMovingAvg = useMemo(
    () => (window > 0 ? computeMovingAverage(sortedAsc, window) : sortedAsc),
    [sortedAsc, window]
  )

  // Filter to the picked window so both the chart and the trend pill
  // reflect that range. Previously the chart showed the full history
  // regardless of the picker, which made the "−X kg over Nd" pill
  // misleading (it was computing total weight change while labelling
  // the picker's day count).
  const filteredAsc = useMemo(() => {
    if (window === 0) return withMovingAvg
    if (withMovingAvg.length === 0) return []
    const lastIso = withMovingAvg[withMovingAvg.length - 1].date
    const cutoffMs =
      new Date(`${lastIso}T00:00:00`).getTime() - (window - 1) * 86400000
    const cutoffIso = new Date(cutoffMs).toISOString().slice(0, 10)
    return withMovingAvg.filter((e) => e.date >= cutoffIso)
  }, [withMovingAvg, window])

  const chartData = useMemo(
    () =>
      filteredAsc.map((e) => ({
        date: new Date(e.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        weight: displayValueWeight(Number(e.weight)),
        movingAvg: e.movingAvg != null ? displayValueWeight(e.movingAvg) : null,
      })),
    [filteredAsc, displayValueWeight]
  )

  // Sample x-axis labels evenly so we never show more than MAX_TICKS
  // regardless of range. Always includes the first and last data points
  // for orientation.
  const tickValues = useMemo(() => {
    if (chartData.length <= MAX_TICKS) return chartData.map((d) => d.date)
    const stride = (chartData.length - 1) / (MAX_TICKS - 1)
    const picked = []
    for (let i = 0; i < MAX_TICKS; i += 1) {
      const idx = Math.round(i * stride)
      picked.push(chartData[idx].date)
    }
    return picked
  }, [chartData])

  // Trend delta: first → last weight across the full visible series.
  // This is what the user actually cares about ("am I down 5 kg or up 2?").
  const trendDelta = useMemo(() => {
    if (chartData.length < 2) return null
    return chartData[chartData.length - 1].weight - chartData[0].weight
  }, [chartData])

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null
    const weight = payload.find((p) => p.dataKey === 'weight')?.value
    const avg = payload.find((p) => p.dataKey === 'movingAvg')?.value
    return (
      <div className="bg-gray-900 dark:bg-gray-800 text-white px-3 py-2 rounded-lg shadow-lg border border-app-primary-500/30 text-sm" dir="ltr">
        <p className="text-muted-foreground text-xs">{label}</p>
        {weight != null && (
          <p className="font-mono font-bold text-app-primary-400">{Number(weight).toFixed(2)} {weightUnit}</p>
        )}
        {avg != null && (
          <p className="font-mono text-xs text-app-primary-300/90 mt-0.5">
            avg: {Number(avg).toFixed(2)} {weightUnit}
          </p>
        )}
      </div>
    )
  }

  if (chartData.length === 0) return null

  // The trend delta gets a color and an arrow icon so the headline
  // change is readable at a glance. We don't editorialize on whether
  // the direction is "good" — the user knows their phase.
  const TrendIcon = trendDelta != null && trendDelta < 0 ? TrendingDown : TrendingUp
  // Label day count: picker value when set; otherwise the actual span
  // between first and last visible entry. Counting entries was wrong —
  // a sparse log spans more days than there are data points.
  const windowDays = useMemo(() => {
    if (window > 0) return window
    if (filteredAsc.length < 2) return 0
    const first = new Date(`${filteredAsc[0].date}T00:00:00`).getTime()
    const last = new Date(`${filteredAsc[filteredAsc.length - 1].date}T00:00:00`).getTime()
    return Math.round((last - first) / 86400000)
  }, [filteredAsc, window])
  const trendCopy =
    trendDelta != null
      ? t('phoenixTracker.weight.trendOverDays', {
          delta: `${trendDelta > 0 ? '+' : ''}${trendDelta.toFixed(1)}`,
          unit: weightUnit,
          n: windowDays,
        })
      : null

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2 flex-wrap">
          <TrendingDown className="w-4 h-4 text-app-primary-500" />
          <span>{t('phoenixTracker.weight.trend')}</span>
          {trendCopy && (
            <span
              className={cn(
                'inline-flex items-center gap-1 text-[11px] font-mono px-2 py-0.5 rounded-full',
                trendDelta < 0
                  ? 'bg-emerald-500/10 text-emerald-500'
                  : 'bg-amber-500/10 text-amber-500'
              )}
              dir="ltr"
            >
              <TrendIcon className="w-3 h-3" />
              {trendCopy}
            </span>
          )}
          <div className="flex gap-1 ms-auto">
            {WINDOWS.map((w) => (
              <button
                key={w}
                onClick={() => setWindow(w)}
                className={cn(
                  'px-2 py-1 rounded-md text-[11px] font-medium font-mono transition-colors',
                  window === w
                    ? 'bg-app-primary-500 text-white'
                    : 'bg-muted text-muted-foreground hover:bg-accent hover:text-foreground'
                )}
              >
                {w === 0 ? t('phoenixTracker.weight.windows.off') : `${w}${t('phoenixTracker.weight.windows.day')}`}
              </button>
            ))}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div dir="ltr" className="w-full">
          <ResponsiveContainer width="100%" height={isMobile ? 220 : 280}>
            <ComposedChart
              data={chartData}
              margin={
                isMobile
                  ? { top: 5, right: 5, left: -20, bottom: 0 }
                  : { top: 5, right: 10, left: -10, bottom: 0 }
              }
            >
              <defs>
                <linearGradient id="weightGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={primary500} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={primary500} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
              <XAxis
                dataKey="date"
                ticks={tickValues}
                tick={{ fontSize: 11, fontFamily: '"JetBrains Mono", monospace' }}
                stroke="#6B7280"
              />
              <YAxis
                domain={['dataMin - 1', 'dataMax + 1']}
                tick={{ fontSize: 11, fontFamily: '"JetBrains Mono", monospace' }}
                stroke="#6B7280"
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="weight"
                stroke={primary500}
                strokeWidth={2}
                fill="url(#weightGradient)"
                dot={{ fill: primary500, strokeWidth: 0, r: 3 }}
                activeDot={{ r: 5, fill: primary500, stroke: '#fff', strokeWidth: 2 }}
              />
              {window > 0 && (
                <Line
                  type="monotone"
                  dataKey="movingAvg"
                  stroke={primaryHsl(700)}
                  strokeWidth={2}
                  dot={false}
                  activeDot={false}
                  isAnimationActive={false}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Legend */}
        <div className="mt-3 pt-3 border-t border-border space-y-2">
          <div className="flex items-start gap-2 text-xs">
            <span
              className="mt-0.5 inline-block w-3 h-3 rounded-sm shrink-0"
              style={{ background: 'linear-gradient(to bottom, hsl(var(--app-primary-500)), hsl(var(--app-primary-500) / 0.1))' }}
              aria-hidden="true"
            />
            <div>
              <span className="font-medium text-foreground">{t('phoenixTracker.weight.legendDailyLabel')}</span>
              <span className="text-muted-foreground"> — {t('phoenixTracker.weight.legendDailyDesc')}</span>
            </div>
          </div>
          {window > 0 && (
            <div className="flex items-start gap-2 text-xs">
              <span
                className="mt-1.5 inline-block w-3 h-0.5 rounded-full shrink-0"
                style={{ backgroundColor: '#C2410C' }}
                aria-hidden="true"
              />
              <div>
                <span className="font-medium text-foreground">
                  {t('phoenixTracker.weight.legendAvgLabel', { window })}
                </span>
                <span className="text-muted-foreground"> — {t('phoenixTracker.weight.legendAvgDesc')}</span>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
